import React, { useState } from 'react';
import { ImportConfirm } from './ImportConfirm';
import { loadOrCreateIdentity } from '../lib/identity';
import { saveProfile } from '../lib/metadata';
import { publishEvent } from '../lib/sync';
import { COLORS, SPACING } from '../styles/theme';
import logoWordmark from '../assets/logo-wordmark.png';

export function Onboarding({ onComplete }: { onComplete: () => void }) {
  const [username, setUsername] = useState('');
  const [displayPreference, setDisplayPreference] = useState<'username' | 'id'>('id');
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [keyCopied, setKeyCopied] = useState(false);
  const [importing, setImporting] = useState(false);

  if (importing) {
    return <ImportConfirm onImported={onComplete} onCancel={() => setImporting(false)} />;
  }

  const identity = loadOrCreateIdentity();
  const shortId = identity.userId.slice(0, 16) + '...';

  const handleCopyId = () => {
    navigator.clipboard?.writeText(identity.userId);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 2000);
  };

  const handleComplete = async () => {
    if (!username.trim() || username.length < 3 || saving) return;
    setSaving(true);
    const profile = {
      userId: identity.userId,
      publicKey: identity.publicKey,
      username: username.trim(),
      displayPreference,
      bio: bio.trim(),
      createdAt: Date.now()
    };
    saveProfile(profile);
    await publishEvent('profile', profile);
    localStorage.setItem('authenticulture_profile_created', 'true');
    onComplete();
  };

  const canSubmit = username.trim().length >= 3 && !saving;

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: COLORS.bg,
      padding: SPACING.lg,
      fontFamily: '"IBM Plex Mono", monospace',
    }}>
      <img src={logoWordmark} alt="Authenticulture" style={{ height: '64px', width: 'auto', marginBottom: SPACING.xl }} />
      <div style={{
        border: `2px outset ${COLORS.highlight}`,
        backgroundColor: COLORS.panel,
        maxWidth: '420px',
        width: '100%',
        overflow: 'hidden',
      }}>
        <div style={{
          backgroundColor: COLORS.accent,
          color: '#fff',
          padding: `${SPACING.sm} ${SPACING.lg}`,
          fontSize: '10px',
          fontWeight: 700,
        }}>
          Authenticulture — Create Profile
        </div>

        <div style={{ padding: SPACING.lg }}>
          <div style={{
            backgroundColor: 'rgba(139,30,45,0.08)',
            border: `1px solid ${COLORS.accent}`,
            padding: SPACING.md,
            marginBottom: SPACING.lg,
          }}>
            <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', fontWeight: 700, color: COLORS.accent, letterSpacing: '0.06em' }}>
              SAVE YOUR ACCOUNT ID
            </p>
            <p style={{ margin: `0 0 ${SPACING.sm} 0`, fontSize: '9px', color: COLORS.text, lineHeight: 1.6 }}>
              Your identity is stored only in this browser. If you clear your storage or switch devices, you will lose access to your account. Save your ID somewhere safe.
            </p>
            <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, flexWrap: 'wrap' }}>
              <code style={{ fontSize: '9px', color: COLORS.textMuted, wordBreak: 'break-all', flex: 1 }}>
                {shortId}
              </code>
              <button
                onClick={handleCopyId}
                style={{
                  background: keyCopied ? COLORS.accent : 'transparent',
                  color: keyCopied ? '#fff' : COLORS.accent,
                  border: `1px solid ${COLORS.accent}`,
                  padding: `${SPACING.xs} ${SPACING.sm}`,
                  cursor: 'pointer',
                  fontSize: '8px',
                  fontFamily: '"IBM Plex Mono", monospace',
                  flexShrink: 0,
                }}
              >
                {keyCopied ? 'Copied' : 'Copy full ID'}
              </button>
            </div>
          </div>

          <div style={{ marginBottom: SPACING.md }}>
            <label style={{ display: 'block', fontSize: '9px', marginBottom: SPACING.xs, color: COLORS.textMuted, fontWeight: 700 }}>
              Username *
            </label>
            <input
              type="text"
              placeholder="3–20 characters"
              value={username}
              onChange={e => setUsername(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleComplete(); }}
              maxLength={20}
              autoFocus
              style={{
                width: '100%',
                padding: SPACING.md,
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '11px',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{ display: 'block', fontSize: '9px', marginBottom: SPACING.xs, color: COLORS.textMuted, fontWeight: 700 }}>
              How should your name appear on posts?
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '11px', color: COLORS.text, cursor: 'pointer', marginBottom: SPACING.xs }}>
              <input type="radio" name="onboardingDisplayPreference" checked={displayPreference === 'username'} onChange={() => setDisplayPreference('username')} />
              My username{username.trim() ? ` (${username.trim()})` : ''}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, fontSize: '11px', color: COLORS.text, cursor: 'pointer' }}>
              <input type="radio" name="onboardingDisplayPreference" checked={displayPreference !== 'username'} onChange={() => setDisplayPreference('id')} />
              My scrambled ID (pseudonymous)
            </label>
          </div>

          <div style={{ marginBottom: SPACING.lg }}>
            <label style={{ display: 'block', fontSize: '9px', marginBottom: SPACING.xs, color: COLORS.textMuted, fontWeight: 700 }}>
              Bio <span style={{ fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea
              placeholder="Say something"
              value={bio}
              onChange={e => setBio(e.target.value)}
              maxLength={200}
              style={{
                width: '100%',
                padding: SPACING.md,
                minHeight: '60px',
                backgroundColor: '#fff',
                color: '#000',
                border: `2px inset ${COLORS.highlight}`,
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: '11px',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
          </div>

          <button
            onClick={handleComplete}
            disabled={!canSubmit}
            style={{
              width: '100%',
              padding: SPACING.lg,
              backgroundColor: canSubmit ? COLORS.accent : COLORS.panel,
              color: canSubmit ? '#fff' : COLORS.textMuted,
              border: `2px outset ${canSubmit ? COLORS.accent : COLORS.border}`,
              cursor: canSubmit ? 'pointer' : 'default',
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: '10px',
              fontWeight: 700,
              textDecoration: 'underline',
            }}
          >
            {saving ? 'Joining network...' : 'Enter Network'}
          </button>

          <button
            onClick={() => setImporting(true)}
            style={{ display: 'block', width: '100%', marginTop: SPACING.md, background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '9px', textDecoration: 'underline', fontFamily: '"IBM Plex Mono", monospace' }}
          >
            Already have an account? Import from another device
          </button>
        </div>
      </div>
    </div>
  );
}
